import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  styled,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  CardActionArea
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllLoanApplications } from "../../API/API";
import IncompleteLoanModal from "../../components/modals/IncompleteLoanModal";
import { toast } from "react-toastify";
import CentralizedTable from "../../components/common/CentralizedTable/CentralizedTable";
import { consultantProposalColumns } from "../../components/common/CentralizedTable/ColumnConfigs";
import { useSearchAndSort } from "../../hooks/useSearchAndSort";

const PageWrapper = styled(Box)({
  backgroundColor: "#F5F6F7",
  minHeight: "100vh",
  padding: "24px",
});

const PageHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
});

const PageTitle = styled(Typography)({
  color: "#0C53B7",
  fontWeight: 600,
  fontSize: "24px",
});

function getProposalSelectedLenderIds(row) {
  const details = row?.proposalDetails;
  const raw = details?.lenderId ?? details?.lenderIds;
  const fromProposal = Array.isArray(raw)
    ? raw
    : raw != null && raw !== ""
      ? [raw]
      : [];
  const fromRow = row?.lenders;
  const fromRowArr = Array.isArray(fromRow)
    ? fromRow
    : fromRow != null && fromRow !== ""
      ? [fromRow]
      : [];
  const merged = [...fromProposal, ...fromRowArr]
    .map((id) => String(id).trim())
    .filter(Boolean);
  return [...new Set(merged)];
}

const ConsultantProposals = () => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [borrowerTypeChoice, setBorrowerTypeChoice] = useState("individual");
  const [entityTypeChoice, setEntityTypeChoice] = useState("company");
  const [error, setError] = useState(null);
  const { searchQuery, setSearchQuery, handleSort, searchAndSortParams, sortBy, order } = useSearchAndSort(500, 'consultant-proposals');
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [loanModalOpen, setLoanModalOpen] = useState(false);
  const navigate = useNavigate();
  const user = useSelector((state) => state.user?.user);

  useEffect(() => {
    if (user && user.UserType !== 'consultant' && user.UserType !== 'Consultant') {
      navigate('/dashboard');
      setSnackbar({
        open: true,
        message: "Access denied. This page is only available for consultants.",
        severity: "error"
      });
    }
  }, [user, navigate]);

  const parseFundRequirement = useCallback((value) => {
    if (!value) return "N/A";

    if (value === "1_5L") return "₹1-5 Lakhs";
    if (value === "5_10L") return "₹5-10 Lakhs";
    if (value === "10-50") return "₹10-50 Lakhs";
    if (value === "50-100") return "₹50-100 Lakhs";

    return value;
  }, []);

  const getFormattedDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy");
    } catch (error) {
      toast.error("Error formatting date:", error);
      return dateString || "N/A";
    }
  }, []);

  const transformProposalData = useCallback((application) => {
    const details = application?.proposalDetails || application?.proposal || null;
    const derivedProposalId =
      details?.id || details?.uuid || null;

    return {
      ...application,
      id: application.uuid || application.id,
      proposalId: derivedProposalId || "N/A",
      applicationId: application?.applicationId || "N/A",
      applicationNumber: application?.applicationNumber || "N/A",
      borrowerName: application?.userId?.name || application?.nameAsPerGST || "N/A",
      mobileNumber: application?.userId?.mobileNumber || application?.mobileNo || "N/A",
      amount: parseFundRequirement(application?.fundRequirement),
      submittedDate: getFormattedDate(application?.createDatetime),
      status: application?.status || "Submitted",
      proposalDetails: details,
      cma: details?.cma,
      onePager: details?.onePager,
      applicationForm: application
    };
  }, [parseFundRequirement, getFormattedDate]);

  useEffect(() => {
    const fetchProposals = async () => {
      try {
        setLoading(true);
        setError(null);

        const userId = user?.ID || user?.id;

        const fieldMap = {
          'proposalId': 'proposalId',
          'applicationId': 'applicationId',
          'applicationNumber': 'applicationNumber',
          'borrowerName': 'borrowerName',
          'mobileNumber': 'mobileNumber',
          'amount': 'fundRequirement',
          'submittedDate': 'createDatetime',
          'status': 'status'
        };

        const apiSortBy = searchAndSortParams.sortBy 
          ? (fieldMap[searchAndSortParams.sortBy] || searchAndSortParams.sortBy)
          : undefined;

        const response = await getAllLoanApplications(null, userId, {
          searchQuery: searchAndSortParams.searchQuery,
          sortBy: apiSortBy,
          order: searchAndSortParams.order
        });
        
        if (response && response.statusCode === 200 && response.result?.data) {
          const transformedData = response.result.data.map(transformProposalData);
          setProposals(transformedData);
        } else {
          setProposals([]);
        }
      } catch (error) {
        toast.error("Error fetching proposals:", error);
        setError(error.message || "Failed to load proposals");
        setSnackbar({
          open: true,
          message: error.message || "Failed to load proposals",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProposals();
  }, [transformProposalData, user, searchAndSortParams.searchQuery, searchAndSortParams.sortBy, searchAndSortParams.order]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, [snackbar]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleViewProposal = useCallback((row) => {
    if (!row?.proposalDetails) {
      setSnackbar({
        open: true,
        message: "Cannot view proposal: Missing proposal details",
        severity: "error",
      });
      return;
    }

    const proposalDetails = row.proposalDetails;
    const proposalIdParam = proposalDetails?.id ?? proposalDetails?.uuid ?? row.id;

    navigate(`/proposals/consultant/details/${proposalIdParam}`, {
      state: {
        proposalData: proposalDetails,
        applicationData: { lenders: getProposalSelectedLenderIds(row) },
      },
    });
  }, [navigate]);

  const handleViewApplication = useCallback((proposal) => {
    setSelectedProposal(proposal);
    setLoanModalOpen(true);
  }, []);

  const handleUpdateProposal = useCallback((proposal) => {
    if (!proposal.applicationId || !proposal.proposalDetails) {
      setSnackbar({
        open: true,
        message: "Cannot update proposal: Missing proposal details",
        severity: "error",
      });
      return;
    }
    navigate(`/proposals/consultant/create/${proposal.applicationId}`, {
      state: {
        proposalData: proposal.proposalDetails,
        isUpdate: true,
        lenders: getProposalSelectedLenderIds(proposal)
      }
    });
  }, [navigate]);

  const handleCreateProposal = useCallback((proposal) => {
    if (!proposal.applicationId) {
      setSnackbar({
        open: true,
        message: "Cannot create proposal: Missing application ID",
        severity: "error",
      });
      return;
    }
    navigate(`/proposals/consultant/create/${proposal.applicationId}`);
  }, [navigate]);

  const openApplyDialog = useCallback(() => {
    if (!entityTypeChoice) {
      setEntityTypeChoice("company");
    }
    setApplyDialogOpen(true);
  }, [entityTypeChoice]);

  const closeApplyDialog = useCallback(() => {
    setApplyDialogOpen(false);
  }, []);

  const handleApplyProceed = useCallback(() => {
    try {
      const businessType = borrowerTypeChoice.toLowerCase();
      const entityType = borrowerTypeChoice.toLowerCase() === "entity" ? entityTypeChoice.toLowerCase() : "individual";
      
      localStorage.setItem("userBusinessType", businessType);
      localStorage.setItem("userEntityType", entityType);
    } catch (e) {
      toast.error("Error setting user business type and entity type");
    }
    setApplyDialogOpen(false);
    navigate("/apply-loan", { 
      state: { 
        fromConsultantDashboard: true,
        source: 'consultant'
      } 
    });
  }, [borrowerTypeChoice, entityTypeChoice, navigate]);

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Proposals</PageTitle>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by Proposal ID, Application ID, Borrower Name, Mobile, Amount, or Status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              minWidth: 400,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#f8f9fa",
                "&:hover": {
                  backgroundColor: "#f0f1f2",
                },
                "&.Mui-focused": {
                  backgroundColor: "white",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },
              },
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={openApplyDialog} sx={{ textTransform: "none" }}>
          Apply Loan for your Clients
          </Button>
        </Box>
      </PageHeader>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress size={40} />
        </Box>
      ) : proposals.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            No proposals found.
          </Typography>
        </Box>
      ) : (
        <CentralizedTable
          data={proposals}
          columns={consultantProposalColumns}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          totalRecords={proposals.length}
          onViewApplication={handleViewApplication}
          onCreateProposal={handleCreateProposal}
          onViewProposal={handleViewProposal}
          onUpdateProposal={handleUpdateProposal}
          showReports={true}
          showOffers={false}
          showActions={true}
          useMaterialTable={true}
          hover={true}
          minWidth="650px"
          onSort={handleSort}
          currentSortBy={sortBy}
          currentOrder={order}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <IncompleteLoanModal
        open={loanModalOpen}
        onClose={() => setLoanModalOpen(false)}
        mode="details"
        loan={selectedProposal}
      />

      <Dialog 
        open={applyDialogOpen} 
        onClose={closeApplyDialog} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            overflow: 'hidden'
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: '#F8FAFC',
          color: '#1F2937',
          padding: '24px 32px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" sx={{ 
                fontSize: '20px', 
                fontWeight: 600, 
                color: '#1F2937',
                margin: 0
              }}>
                Start New Application
              </Typography>
              <Typography sx={{ 
                fontSize: '14px', 
                color: '#6B7280',
                marginTop: '4px'
              }}>
                Choose your borrower type to begin the loan application process
              </Typography>
            </Box>
            <IconButton 
              onClick={closeApplyDialog} 
              size="small"
              sx={{ 
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                  color: '#374151'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ 
          padding: '24px 32px 0 32px'
        }}>
          <Typography sx={{ 
            fontSize: '16px', 
            fontWeight: 600, 
            color: '#374151', 
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <PersonIcon sx={{ fontSize: '20px', color: '#6B7280' }} />
            Select Borrower Type
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Card 
              sx={{ 
                flex: 1,
                border: `2px solid ${borrowerTypeChoice === "individual" ? '#3B82F6' : '#E5E7EB'}`,
                borderRadius: '8px',
                background: borrowerTypeChoice === "individual" ? '#F0F9FF' : '#FFFFFF',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#3B82F6',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
                }
              }}
              onClick={() => setBorrowerTypeChoice("individual")}
            >
              <CardActionArea>
                <CardContent sx={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  '&:last-child': { paddingBottom: '20px' }
                }}>
                  <Box sx={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    background: borrowerTypeChoice === "individual" ? '#3B82F6' : '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                    color: borrowerTypeChoice === "individual" ? '#FFFFFF' : '#6B7280'
                  }}>
                    <PersonIcon sx={{ fontSize: '24px' }} />
                  </Box>
                  <Typography sx={{ 
                    fontSize: '16px', 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: '4px'
                  }}>
                    Individual
                  </Typography>
                  <Typography sx={{ 
                    fontSize: '14px', 
                    color: '#6B7280', 
                    lineHeight: 1.5
                  }}>
                    Personal loan application for individual borrowers
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>

            <Card 
              sx={{ 
                flex: 1,
                border: `2px solid ${borrowerTypeChoice === "entity" ? '#3B82F6' : '#E5E7EB'}`,
                borderRadius: '8px',
                background: borrowerTypeChoice === "entity" ? '#F0F9FF' : '#FFFFFF',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#3B82F6',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
                }
              }}
              onClick={() => setBorrowerTypeChoice("entity")}
            >
              <CardActionArea>
                <CardContent sx={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  '&:last-child': { paddingBottom: '20px' }
                }}>
                  <Box sx={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    background: borrowerTypeChoice === "entity" ? '#3B82F6' : '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                    color: borrowerTypeChoice === "entity" ? '#FFFFFF' : '#6B7280'
                  }}>
                    <BusinessIcon sx={{ fontSize: '24px' }} />
                  </Box>
                  <Typography sx={{ 
                    fontSize: '16px', 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: '4px'
                  }}>
                    Entity
                  </Typography>
                  <Typography sx={{ 
                    fontSize: '14px', 
                    color: '#6B7280', 
                    lineHeight: 1.5
                  }}>
                    Business loan application for companies and organizations
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Box>

          {borrowerTypeChoice === "entity" && (
            <Box sx={{ 
              marginTop: '16px',
              padding: '20px',
              background: '#F9FAFB',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <Typography sx={{ 
                fontSize: '16px', 
                fontWeight: 600, 
                color: '#374151', 
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <AccountBalanceIcon sx={{ fontSize: '20px', color: '#6B7280' }} />
                Entity Type
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="entity-type-label">Select Entity Type</InputLabel>
                <Select
                  labelId="entity-type-label"
                  value={entityTypeChoice}
                  label="Select Entity Type"
                  onChange={(e) => setEntityTypeChoice(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D1D5DB'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3B82F6'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3B82F6',
                      borderWidth: '2px'
                    }
                  }}
                >
                  <MenuItem value="company">Company</MenuItem>
                  <MenuItem value="partnership_firm">Partnership Firm</MenuItem>
                  <MenuItem value="propreitorship_firm">Proprietorship Firm</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          padding: '16px 32px 24px 32px',
          background: '#FFFFFF',
          borderTop: '1px solid #E5E7EB',
          gap: '12px',
          marginTop: '0'
        }}>
          <Button 
            variant="outlined" 
            onClick={closeApplyDialog}
            sx={{
              borderRadius: '6px',
              padding: '10px 20px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              borderColor: '#D1D5DB',
              color: '#6B7280',
              '&:hover': {
                borderColor: '#9CA3AF',
                backgroundColor: '#F9FAFB',
                color: '#374151'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleApplyProceed}
            sx={{
              borderRadius: '6px',
              padding: '10px 24px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: '#3B82F6',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#2563EB'
              }
            }}
          >
            Proceed to Application
          </Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};

export default ConsultantProposals;
